/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import Imgix from 'js/components/Imgix';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Link, Typography } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { getLandingPageLink, logo } from 'bundles/coursera-plus/utils/courseraPlusUtils';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import DottedSeparator from 'bundles/unified-description-page-common/components/shared/DottedSeparator';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';

import _t from 'i18n!nls/xdp';

type PropsFromCaller = {
  isSubscribedToCourseraLite?: boolean;
  disableCTA?: boolean;
  size?: 'small';
};

type PropsToComponent = PropsFromCaller & { isSubscribedToCPlusOrLite?: boolean };

const styles = {
  container: css`
    display: flex;
    align-items: center;
  `,
  courseraPlusLogoImage: css`
    position: relative;
    bottom: 1px;
  `,
  learnMoreLink: css`
    text-decoration-color: var(--cds-color-grey-975);
  `,
};

export const HeroCourseraPlusOrLiteCta = ({
  isSubscribedToCPlusOrLite,
  isSubscribedToCourseraLite,
  disableCTA,
  size,
}: PropsToComponent) => {
  const eventingProductData = useEventingProductData();
  const track = useTracker();

  const onClick = () => {
    track('click_button', {
      button: { name: 'description_page_c_plus_learn_more' },
      pageSection: {
        sectionName: 'hero_banner',
      },
      ...eventingProductData,
    });
  };

  return (
    <div css={styles.container} data-e2e="hero-coursera-plus-cta">
      <Typography variant={size === 'small' ? 'body2' : 'body1'} component="p">
        <FormattedMessage
          message={_t('Included with {courseraPlusOrLite}')}
          courseraPlusOrLite={
            <Imgix
              src={isSubscribedToCourseraLite ? logo.LITE_FULL_BLUE_HD : logo.PLUS_FULL_BLUE}
              maxHeight={size === 'small' ? 10 : 12}
              alt={isSubscribedToCourseraLite ? _t('Coursera Lite') : _t('Coursera Plus')}
              css={styles.courseraPlusLogoImage}
            />
          }
        />
      </Typography>
      {!isSubscribedToCPlusOrLite && !disableCTA && (
        <div css={styles.container}>
          <DottedSeparator />
          <Link
            component={TrackedLink2}
            href={getLandingPageLink()}
            trackingName="coursera_plus_banner"
            css={styles.learnMoreLink}
            aria-label={_t('Learn more about Coursera Plus')}
            onClick={onClick}
          >
            <Typography
              component="span"
              variant={size === 'small' ? 'body2' : 'body1'}
              data-e2e="hero-coursera-plus-learn-more"
            >
              {_t('Learn more')}
            </Typography>
          </Link>
        </div>
      )}
    </div>
  );
};

export default HeroCourseraPlusOrLiteCta;
